import { AuthApiService } from '@revenuewell/logic-http';
import { NavigationService } from '@revenuewell/logic-navigation';
import { OidcService } from '@revenuewell/logic-oidc';
import {
    CancelToken,
    ClientApp,
    FormAssignmentsIndexResponse,
    FormListResponse,
    GetPatientResponse,
    Invite,
    LegacyFormSubmissionDto,
    PacketListResponse,
    PatientDto,
    PatientInviteMethod,
    UserInfo
} from './forms-service-types';

export default class FormsService extends AuthApiService {
    private static instance: FormsService;
    private config: any;

    constructor(config: any, oidcService: OidcService) {
        super(oidcService, new NavigationService());
        this.config = config;
    }

    public static getInstance(config: any, oidcService: OidcService) {
        if (!FormsService.instance) FormsService.instance = new FormsService(config, oidcService);

        return FormsService.instance;
    }

    private fetch = async (url: string, options?: any): Promise<Response> => {
        //
        const { headers } = await this.addRequestHeader(options);
        return this.authFetch(url, {
            ...options,
            headers: {
                ...options?.headers,
                ...headers,
                rwaccountid: headers.CustomerId,
                rwlocationids: headers.LocationId ? `[${headers.LocationId}]` : undefined
            }
        });
    };

    getFormsUnread = async (locationId: number | undefined): Promise<boolean> => {
        let url = `${await this.config.forms2.apiUrl}/api/FormSubmissions/GetSubmissionsAnyUnread`;
        if (locationId) {
            url += `?LocationId=${locationId}`;
        }
        const response = await this.fetch(url);

        return await response.json();
    };


    getFormsUnreadCount = async (locationId: number | undefined): Promise<number> => {
        let url = `${await this.config.forms2.apiUrl}/api/FormSubmissions/GetUnreadSubmissionsCount`;
        if (locationId) {
            url += `?LocationId=${locationId}`;
        }
        const response = await this.fetch(url);

        return await response.json();
    }

    getById = async (patientId: number): Promise<PatientDto> => {
        const url = `${await this.config.forms2.apiUrl}/api/FormAssignments/Get?PatientId=${patientId}`;

        const response = await this.fetch(url);

        const data = (await response.json()) as GetPatientResponse;

        return data.patients.find(p => p.id === patientId) as PatientDto;
    };

    getAllForms = async (patientId: number): Promise<GetPatientResponse> => {
        const url = `${await this.config.forms2.apiUrl}/api/FormAssignments/Get?PatientId=${patientId}`;

        const response = await this.fetch(url);

        const data = (await response.json()) as GetPatientResponse;

        return data;
    };

    invite = async (
        patientId: number | undefined,
        method: PatientInviteMethod | undefined,
        cancelToken?: CancelToken | undefined
    ): Promise<Invite> => {
        const baseUrl = `${await this.config.forms2.apiUrl}/api/Patients/Invite`;

        const url = new URL(baseUrl);

        if (patientId === null) {
            throw new Error("The parameter 'patientId' cannot be null.");
        }

        if (method === null) {
            throw new Error("The parameter 'method' cannot be null.");
        }

        const url_ = url.toString();

        const response = await this.fetch(url_, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json'
            },
            body: JSON.stringify({ patientId, method })
        });

        return await response.json();
    };

    goToScanUrl = async (): Promise<string> => {
        return `${await this.config.forms2.apiUrl}/patients/scan`;
    };

    unassignAllForms = async (patientId: number): Promise<void> => {
        const url = `${await this.config.forms2.apiUrl}/api/FormAssignments`;

        const response = await this.fetch(url, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json'
            },
            body: JSON.stringify({ rwPatientId: patientId })
        });

        return response.json();
    };

    getUser = async (): Promise<UserInfo> => {
        const url = `${await this.config.forms2.apiUrl}/api/Users`;

        const response = await this.fetch(url);

        return response.json();
    };

    addOrUpdatePin = async (pin: string): Promise<void> => {
        const url = `${await this.config.forms2.apiUrl}/api/Users/AddUpdateUserPin`;

        const response = await this.fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json'
            },
            body: JSON.stringify({ pin: Number(pin) })
        });

        return response.json();
    };

    getFormAssignmentsIndex = async (): Promise<FormAssignmentsIndexResponse> => {
        const url = `${await this.config.forms2.apiUrl}/api/FormAssignments/GetFormAssignmentsIndexVm`;

        const response = await this.fetch(url);

        return response.json();
    };

    listForms = async ({
        page = 0,
        pageSize = 10,
        search = '',
        sortCol = '',
        sortDir = '',
        filterByFormType,
        filterBySpecialty,
        locationId = 0,
        listType = '0',
        excludeHidden = true,
        client = 0
    }: {
        page?: number;
        pageSize?: number;
        search?: string;
        sortCol?: string;
        sortDir?: string;
        filterByFormType?: string;
        filterBySpecialty?: string;
        locationId?: number;
        listType?: string;
        excludeHidden?: boolean;
        client?: ClientApp;
    }): Promise<FormListResponse> => {
        const baseUrl = `${await this.config.forms2.apiUrl}/api/Forms/List`;
        const url = new URL(baseUrl);
        const params = new URLSearchParams();

        console.log('filterBySpecialty', filterBySpecialty);
        // Default values will be used if parameters are not provided
        params.append('Page', page.toString());
        params.append('PageSize', pageSize.toString());
        params.append('Search', encodeURIComponent(search));
        params.append('SortCol', encodeURIComponent(sortCol));
        params.append('SortDir', encodeURIComponent(sortDir));
        if (filterByFormType != null) params.append('FilterByFormType', filterByFormType.toString());
        params.append('FilterBySpecialty', encodeURIComponent(filterBySpecialty ?? ''));
        params.append('LocationId', locationId.toString());
        params.append('ListType', listType.toString());
        params.append('ExcludeHidden', excludeHidden.toString());
        params.append('Client', client.toString());

        url.search = params.toString();

        const options: RequestInit = {
            method: 'GET',
            headers: {
                Accept: 'application/json'
            }
        };

        const response = await this.fetch(url.toString(), options);
        return response.json();
    };

    listPackets = async ({
        page = 0,
        pageSize = 10,
        locationId = 0,
        search = '',
        sortCol = '',
        sortDir = '',
        includeNames = true,
        formLocationId = 0,
        client = 0
    }: {
        page?: number;
        pageSize?: number;
        locationId?: number;
        search?: string;
        sortCol?: string;
        sortDir?: string;
        includeNames?: boolean;
        formLocationId?: number;
        client?: ClientApp;
        cancelToken?: CancelToken;
    }): Promise<PacketListResponse> => {
        const url = new URL(this.config.forms2.apiUrl + '/api/FormPackets/List');
        const params = new URLSearchParams();

        params.append('Page', page.toString());
        params.append('PageSize', pageSize.toString());
        params.append('LocationId', locationId.toString());
        params.append('Search', search);
        params.append('SortCol', sortCol);
        params.append('SortDir', sortDir);
        params.append('IncludeNames', includeNames.toString());
        params.append('FormLocationId', formLocationId.toString());
        params.append('Client', client.toString());

        url.search = params.toString();

        const response = await this.fetch(url.toString());

        return response.json();
    };

    assignForms = async ({
        formIds,
        packetIds,
        contact
    }: {
        formIds: string[];
        packetIds: string[];
        contact: {
            id: number;
            firstName: string;
            lastName: string;
            email: string;
            dateOfBirth: string;
            locationId: number;
        };
    }): Promise<void> => {
        const url = `${await this.config.forms2.apiUrl}/api/FormAssignments`;

        const response = await this.fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json'
            },
            body: JSON.stringify({ formIds, packetIds, contact })
        });

        return response.json();
    };

    unassignForm = async (formId: string): Promise<void> => {
        const url = `${await this.config.forms2.apiUrl}/api/FormAssignments/${formId}`;

        const response = await this.fetch(url, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json'
            },
            body: JSON.stringify({ id: formId })
        });

        return response.json();
    };

    getPdf = async ({ formId, isSubmitted = false }: { formId: string; isSubmitted?: boolean }): Promise<Blob> => {
        const url = `${await this.config.forms2.apiUrl}/api/${isSubmitted ? 'FormSubmissions' : 'Forms'
            }/GetPdf?Id=${formId}`;

        const response = await this.fetch(url, {
            headers: {
                Accept: 'application/octet-stream'
            }
        });

        if (isSubmitted) {
            const url = await response.json();
            const downloadBlob = await fetch(url, {
                headers: {
                    Accept: 'application/octet-stream'
                }
            });

            return downloadBlob.blob();
        }

        return response.blob();
    };

    updatePatientConsent = async (patientId: number): Promise<void> => {
        const url = `${await this.config.forms2.apiUrl}/api/FormAssignments/UpdatePatientConsent`;

        const response = await this.fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json'
            },
            body: JSON.stringify({ rwPatientId: patientId })
        });

        return response.json();
    };

    getIsMigratedToForms2 = async (locationId: number): Promise<boolean> => {
        const url = `${await this.config.forms2.apiUrl}/api/Locations/GetIsMigratedToForms2?LocationId=${locationId}`;
        const response = await this.fetch(url);

        return response.json();
    };

    getLegacySubmissions = async (patientId: number, locationId?: number): Promise<LegacyFormSubmissionDto[]> => {
        const url = `${await this.config.forms2.apiUrl}/api/FormSubmissions/GetLegacySubmissionsForPatient?PatientId=${patientId}${locationId ? `&LocationId=${locationId}` : ''}`;
        const response = await this.fetch(url, {
            headers: locationId
                ? {
                    LocationId: locationId.toString(),
                    rwlocationids: `[${locationId}]`
                }
                : undefined
        });
        return response.json();
    };

    getLegacyPdfUrl = (formId: number): string => {
        return `${this.config.practice.url}/Review/PdfContent/${formId}`;
    };
}
